/**
 * Default evaluation template for demo and seeding purposes
 * Contains factory functions for creating sample evaluation templates
 */

import { Importance } from '@/types/evaluation.types';
import type {
  EvaluationTemplate,
  Criterion,
  CriteriaGroup,
} from '@/types/evaluation.types';

/**
 * Creates a default evaluation template with sample criteria
 * Useful for seeding new installations, testing, or providing examples
 *
 * @param generateId - Function to generate unique IDs for entities
 * @returns A fully populated EvaluationTemplate with three criteria groups
 *
 * @example
 * ```typescript
 * import { generateId } from '@/lib/id';
 * import { createDefaultEvaluationTemplate } from '@/types/evaluation.fixtures';
 *
 * const template = createDefaultEvaluationTemplate(generateId);
 * ```
 */
export function createDefaultEvaluationTemplate(
  generateId: () => string
): EvaluationTemplate {
  // Functional Requirements Criteria
  const functionalCriteria: Criterion[] = [
    {
      id: generateId(),
      title: 'Core Feature Set',
      description:
        'Evaluate whether the solution provides all core features required for the use case',
      importance: Importance.Critical,
      category: 'Features',
      helpfulHints: 'Consider must-have features vs. nice-to-have features',
    },
    {
      id: generateId(),
      title: 'User Interface & Experience',
      description: 'Assess the intuitiveness and usability of the user interface',
      importance: Importance.High,
      category: 'Usability',
    },
    {
      id: generateId(),
      title: 'Integration Capabilities',
      description:
        'Evaluate the ability to integrate with existing systems and tools',
      importance: Importance.High,
      category: 'Technical',
      helpfulHints: 'Check for API availability, webhooks, and connectors',
    },
  ];

  // Technical Capabilities Criteria
  const technicalCriteria: Criterion[] = [
    {
      id: generateId(),
      title: 'Performance & Scalability',
      description: 'Assess system performance under load and ability to scale',
      importance: Importance.High,
      category: 'Performance',
    },
    {
      id: generateId(),
      title: 'Security & Compliance',
      description:
        'Evaluate security features and compliance with industry standards',
      importance: Importance.Critical,
      category: 'Security',
      helpfulHints:
        'Consider data encryption, access controls, audit logs, and certifications',
    },
    {
      id: generateId(),
      title: 'Reliability & Uptime',
      description: 'Assess system reliability and availability guarantees',
      importance: Importance.High,
      category: 'Reliability',
    },
  ];

  // Business Considerations Criteria
  const businessCriteria: Criterion[] = [
    {
      id: generateId(),
      title: 'Pricing & Total Cost of Ownership',
      description:
        'Evaluate pricing model and total cost including implementation and maintenance',
      importance: Importance.Critical,
      category: 'Cost',
    },
    {
      id: generateId(),
      title: 'Vendor Support & Documentation',
      description:
        'Assess quality of vendor support, documentation, and training resources',
      importance: Importance.Medium,
      category: 'Support',
    },
    {
      id: generateId(),
      title: 'Implementation Timeline',
      description: 'Evaluate estimated time to implement and get to production',
      importance: Importance.Medium,
      category: 'Timeline',
    },
  ];

  // Organize criteria into weighted groups
  const criteriaGroups: CriteriaGroup[] = [
    {
      id: generateId(),
      name: 'Functional Requirements',
      weight: 40,
      criteria: functionalCriteria,
    },
    {
      id: generateId(),
      name: 'Technical Capabilities',
      weight: 35,
      criteria: technicalCriteria,
    },
    {
      id: generateId(),
      name: 'Business Considerations',
      weight: 25,
      criteria: businessCriteria,
    },
  ];

  // Create and return the template
  return {
    id: generateId(),
    templateName: 'Default Evaluation Template',
    criteriaGroups,
    modifiedDate: new Date(),
  };
}
